/*
  React Query hooks for the Phase 2 deep-dive screens.

  Two rules from the contract are enforced here rather than at each call site:

  · **Cadence.** Health and incidents may poll at any rate; sales and messaging
    must not poll faster than every 60 s, because the backend caches for 60 s and
    the upstream behind it is expensive. So the sales and messaging hooks share
    one interval constant and nothing may set its own.

  · **Retry.** A 503 means the data source was never connected (the DocuSign
    envelope routes live there until OWEHub ships) and a 400 means we sent a bad
    parameter. Retrying either just burns requests and delays the screen showing
    the honest "not connected" state, so both fail fast; a slow or unusable
    upstream (502/504) gets one more go.
*/

import {
  useMutation,
  useQueries,
  useQueryClient,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { ApiError } from '@/lib/api/caller'
import { deepDiveKeys } from '@/lib/api/query-keys'
import {
  getEnvelopeGroups,
  getEnvelopes,
  getEnvelopesOverview,
  getIncidents,
  getPlatformIncidents,
  getMessagingAlerts,
  getMessagingHourly,
  getMessagingOverview,
  getMessagingRejections,
  getSalesFilters,
  getSalesOverview,
  getSalesQuarterly,
  getSalesSeries,
  getSalesSummary,
  getSalesTimeline,
  getServiceOverview,
  getVendorStatus,
  remindEnvelope,
  resendEnvelopeLink,
  salesFilterPlatform,
} from '@/lib/api/deep-dive'
import type {
  DeepDiveService,
  EnvelopeGroupBy,
  EnvelopesQuery,
  MessagingProvider,
  MessagingWindow,
  SalesFilterSelection,
  SalesPeriodSelection,
} from '@/lib/api/deep-dive.types'
import { windowStart, type IncidentWindow } from '@/lib/platform-incidents'

/** Health and incidents are cheap and locally computed — poll on the half minute. */
const HEALTH_POLL_MS = 30_000

/** The contract's floor for sales and messaging. Do not lower it. */
export const SLOW_POLL_MS = 60_000

/** Statuses where another attempt cannot change the answer. */
const TERMINAL = new Set([400, 401, 403, 503])

function retryDeepDive(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && TERMINAL.has(error.status)) return false
  return failureCount < 2
}

/** Shared query behaviour; every hook below starts from this. */
function deepDiveQuery<T>(
  options: UseQueryOptions<T, Error, T, readonly unknown[]>,
): UseQueryOptions<T, Error, T, readonly unknown[]> {
  return { retry: retryDeepDive, refetchOnWindowFocus: false, ...options }
}

/* ------------------------------------------------------------------ health */

/** Status, response time, 30-day uptime and the open incident, if any. */
export function useServiceOverview(service: DeepDiveService) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.overview(service),
      queryFn: () => getServiceOverview(service),
      refetchInterval: HEALTH_POLL_MS,
    }),
  )
}

/**
 * How many incidents to pull for the log — the endpoint's maximum.
 *
 * The whole 30-day window is fetched in one request and paged in the browser,
 * rather than walking `offset` a page at a time, because the indicator filter on
 * that table has no server-side equivalent (`{svc}/incidents` takes only
 * from/to/limit/offset). Paging on the server would leave the filter narrowing
 * just the visible page, which reports wrong counts. Volumes make this cheap:
 * a trailing 30 days is single or low double digits per service today.
 *
 * `total` on the response is what says whether this ceiling was hit — the table
 * shows a note when it was, which is the signal to move to server-side paging.
 */
export const INCIDENT_FETCH_LIMIT = 200

/** The trailing-30-day incident log. `limit` is 1–200. */
export function useIncidents(service: DeepDiveService, limit = INCIDENT_FETCH_LIMIT) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.incidents(service, limit),
      queryFn: () => getIncidents(service, { limit }),
      refetchInterval: HEALTH_POLL_MS,
    }),
  )
}

/** One round is a request per platform, so this polls slower than a single system's log. */
const PLATFORM_INCIDENTS_POLL_MS = 60_000

/**
 * Incident logs for several catalog platforms over one window, one request each
 * (the route is per key). Results come back in `keys` order.
 */
export function usePlatformIncidents(
  keys: readonly string[],
  period: IncidentWindow,
  enabled = true,
) {
  return useQueries({
    queries: keys.map((key) =>
      deepDiveQuery({
        queryKey: deepDiveKeys.platformIncidents(key, period),
        // `from` is taken at fetch time so a refetch slides the window forward.
        queryFn: () =>
          getPlatformIncidents(key, { from: windowStart(period), limit: INCIDENT_FETCH_LIMIT }),
        enabled,
        refetchInterval: PLATFORM_INCIDENTS_POLL_MS,
      }),
    ),
  })
}

/* ------------------------------------------------------------------- sales */

export function useSalesOverview(
  service: DeepDiveService,
  filters: SalesFilterSelection,
  date?: string,
) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.salesOverview(service, date, filters),
      queryFn: () => getSalesOverview(service, date, filters),
      refetchInterval: SLOW_POLL_MS,
      // The upstream is expensive and the backend caches for 60s anyway, so a
      // cached answer stays good for as long as that cache would have been.
      staleTime: SLOW_POLL_MS,
    }),
  )
}

export function useSalesTimeline(
  service: DeepDiveService,
  filters: SalesFilterSelection,
  date?: string,
) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.salesTimeline(service, date, filters),
      queryFn: () => getSalesTimeline(service, date, filters),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

export function useSalesQuarterly(
  service: DeepDiveService,
  filters: SalesFilterSelection,
  year?: number,
) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.salesQuarterly(service, year, filters),
      queryFn: () => getSalesQuarterly(service, year, filters),
      // A quarterly roll-up does not move minute to minute; refetch on mount and
      // leave it alone. The 60s floor still applies to the manual path.
      staleTime: 15 * 60_000,
    }),
  )
}

/**
 * Headline totals for a period, against the same span one step back.
 *
 * A custom period is only requested once both ends are set. A half-filled range
 * is a guaranteed 400 (`Missing from/to`), and firing it would flash an error on
 * screen while someone is still picking the second date.
 */
export function useSalesSummary(
  service: DeepDiveService,
  period: SalesPeriodSelection,
  filters: SalesFilterSelection,
) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.salesSummary(service, period, filters),
      queryFn: () => getSalesSummary(service, period, filters),
      enabled: isPeriodComplete(period),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

/**
 * The same period, bucketed for the chart.
 *
 * Bucket size is the backend's decision, carried on `period.granularity` — the
 * client never derives it. A nine-month span drawn on a 24-hour axis reads
 * perfectly well and means nothing.
 */
export function useSalesSeries(
  service: DeepDiveService,
  period: SalesPeriodSelection,
  filters: SalesFilterSelection,
) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.salesSeries(service, period, filters),
      queryFn: () => getSalesSeries(service, period, filters),
      enabled: isPeriodComplete(period),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

/** False while a custom range is still missing an end. */
export function isPeriodComplete(period: SalesPeriodSelection): boolean {
  return period.key !== 'custom' || Boolean(period.from && period.to)
}

/**
 * The filter catalogue for a system's platform.
 *
 * Upstream caches it for 15 minutes (`cache_ttl_seconds: 900`), so there is
 * nothing to gain from asking more often than that. Disabled for DocuSign, which
 * has health endpoints but no sales ones.
 */
export function useSalesFilters(service: DeepDiveService) {
  const platform = salesFilterPlatform(service)
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.salesFilters(platform ?? 'aurora'),
      queryFn: () => getSalesFilters(platform!),
      enabled: platform !== null,
      staleTime: 15 * 60_000,
    }),
  )
}

/* ---------------------------------------------------------------- messaging */

/*
  One hook per route, `provider` picking the family: `sendgrid` for the email
  screen, `twilio` for SMS. No `channel` is sent — each family serves its own
  channel and rejects the other's with a 400.

  Both families sit behind the same 60 s floor, and because they are keyed apart
  a failure on one screen cannot touch the other's data.
*/

export function useMessagingOverview(provider: MessagingProvider, window: MessagingWindow) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.messaging(window, provider),
      queryFn: () => getMessagingOverview(provider, window),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

export function useMessagingHourly(provider: MessagingProvider, window: MessagingWindow) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.messagingHourly(window, provider),
      queryFn: () => getMessagingHourly(provider, window),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

/**
 * The most recent rejections.
 *
 * Deliberately a single page rather than a cursor walk: this panel is the "last
 * few failures" readout beneath the counters, and the cursor exists for a full
 * log page that does not exist yet. Raising `limit` is how this panel shows more.
 */
export function useMessagingRejections(provider: MessagingProvider, limit = 25) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.messagingRejections(limit, provider),
      queryFn: () => getMessagingRejections(provider, limit),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

/** The blockage inference behind the alert banner. */
export function useMessagingAlerts(provider: MessagingProvider) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.messagingAlerts(provider),
      queryFn: () => getMessagingAlerts(provider),
      refetchInterval: SLOW_POLL_MS,
      staleTime: SLOW_POLL_MS,
    }),
  )
}

/* ------------------------------------------------------------ vendor status */

/**
 * A vendor's status page and account probe. Cheap and locally cached by the
 * backend, so it polls on the health cadence rather than the messaging floor.
 */
export function useVendorStatus(vendor: string) {
  return useQuery(
    deepDiveQuery({
      queryKey: [...deepDiveKeys.all, 'vendor-status', vendor] as const,
      queryFn: () => getVendorStatus(vendor),
      refetchInterval: HEALTH_POLL_MS,
    }),
  )
}

/* ---------------------------------------------------------------- envelopes */

/*
  These answer 502/503 until the OWEHub e-sign API is deployed. `retryDeepDive`
  stops a 503 being retried, so the screen reaches its "not connected" state on
  the first response rather than after three timeouts.
*/

export function useEnvelopesOverview(period: SalesPeriodSelection) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.envelopesOverview(period),
      queryFn: () => getEnvelopesOverview(period),
      refetchInterval: SLOW_POLL_MS,
    }),
  )
}

export function useEnvelopes(query: EnvelopesQuery) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.envelopes(query),
      queryFn: () => getEnvelopes(query),
      refetchInterval: SLOW_POLL_MS,
    }),
  )
}

export function useEnvelopeGroups(groupBy: EnvelopeGroupBy, period: SalesPeriodSelection) {
  return useQuery(
    deepDiveQuery({
      queryKey: deepDiveKeys.envelopeGroups(groupBy, period),
      queryFn: () => getEnvelopeGroups(groupBy, period),
      refetchInterval: SLOW_POLL_MS,
    }),
  )
}

/** One row's chase action: a reminder for the envelope, or a fresh link for a signer. */
export interface EnvelopeActionInput {
  envelopeId: string
  recipientId: string
  action: 'send_reminder' | 'resend_link'
}

/**
 * Chase an envelope — the only two writes on these screens.
 *
 * Both need the Phase 1 JWT and a role of org_admin / platform_admin /
 * management_user, so a 401 or 403 here is a real answer to render, not a bug.
 * Deliberately not retried: every failure mode is terminal (wrong role, terminal
 * envelope, cooldown), and a retried reminder is a second email to a customer.
 *
 * The envelope list is invalidated on success so the row's suggested action
 * comes back from the server rather than being guessed at locally.
 */
export function useEnvelopeAction() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: ({ envelopeId, recipientId, action }: EnvelopeActionInput) =>
      action === 'send_reminder'
        ? remindEnvelope(envelopeId)
        : resendEnvelopeLink(envelopeId, recipientId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...deepDiveKeys.all, 'envelopes'] })
    },
  })
}
